import React, { useEffect, useState } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Error,
  Inventory,
} from '@mui/icons-material';
import { useBundleCreation } from '../../contexts/BundleCreationContext';
import { validateBundleStockWithShelfDetails } from '../../utils/inventoryUtils';

const StockValidator: React.FC = () => {
  const { state, dispatch } = useBundleCreation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailedValidation, setDetailedValidation] = useState<any>(null);

  // Enhanced validation that includes shelf location details
  useEffect(() => {
    if (!state.selectedBundleSKU || state.quantity <= 0) {
      setDetailedValidation(null);
      return;
    }

    const validateWithShelfDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Get detailed validation with shelf locations
        const enhancedValidation = await validateBundleStockWithShelfDetails(
          state.selectedBundleSKU!.childSKUs,
          state.quantity
        );

        setDetailedValidation(enhancedValidation);

        // Update the context with the enhanced validation
        dispatch({ type: 'SET_VALIDATION_RESULT', payload: enhancedValidation });

      } catch (err) {
        console.error('Error validating stock:', err);
        setError('Failed to validate stock availability');
        setDetailedValidation({
          isValid: false,
          errors: ['Failed to validate stock availability'],
          childSKUAvailability: []
        });
      } finally {
        setIsLoading(false);
      }
    };

    validateWithShelfDetails();
  }, [state.selectedBundleSKU, state.quantity, dispatch]);

  if (!state.selectedBundleSKU) {
    return (
      <Alert severity="warning">
        Please complete the previous steps first.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography>Validating stock availability...</Typography>
        <LinearProgress sx={{ width: '100%', mt: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to validate stock. Please try again later.
      </Alert>
    );
  }

  const getStockStatus = (required: number, available: number) => {
    if (available >= required) {
      return { status: 'sufficient', color: 'success', icon: <CheckCircle /> };
    } else if (available > 0) {
      return { status: 'insufficient', color: 'warning', icon: <Warning /> };
    } else {
      return { status: 'unavailable', color: 'error', icon: <Error /> };
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory />
        Stock Validation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Checking availability of child SKUs for {state.quantity} bundle(s)
      </Typography>

      {/* Validation Summary */}
      {detailedValidation && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {detailedValidation.isValid ? (
                <CheckCircle color="success" />
              ) : (
                <Warning color="warning" />
              )}
              <Typography variant="h6">
                {detailedValidation.isValid
                  ? 'Stock Available'
                  : 'Stock Issues Detected'}
              </Typography>
            </Box>

            {!detailedValidation.isValid && detailedValidation.errors.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Stock Issues:
                </Typography>
                {detailedValidation.errors.map((error: string, index: number) => (
                  <Typography key={index} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stock Details Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Child SKU</TableCell>
              <TableCell align="right">Required</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell>Shelf Locations</TableCell>
              <TableCell align="center">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {detailedValidation?.childSKUAvailability.map((availability: any) => {
              const stockInfo = getStockStatus(availability.requiredQuantity, availability.availableQuantity);

              return (
                <TableRow key={availability.skuId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {availability.name || availability.skuId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      SKU: {availability.skuId}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {availability.requiredQuantity}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      color={availability.availableQuantity >= availability.requiredQuantity ? 'success.main' : 'error.main'}
                    >
                      {availability.availableQuantity}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {availability.shelfLocations && availability.shelfLocations.length > 0 ? (
                        availability.shelfLocations.map((location: any, index: number) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={location.shelfLocation}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              Qty: {location.quantity}
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {isLoading ? 'Loading shelf locations...' : 'No shelf locations found'}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      icon={stockInfo.icon}
                      label={availability.isAvailable ? 'Available' : 'Insufficient'}
                      color={availability.isAvailable ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Show loading state in table */}
            {isLoading && !detailedValidation && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, py: 4 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Loading stock validation...
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {/* Show error state in table */}
            {error && !detailedValidation && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Alert severity="error" sx={{ my: 2 }}>
                    {error}
                  </Alert>
                </TableCell>
              </TableRow>
            )}

            {/* Show empty state */}
            {!isLoading && !error && (!detailedValidation || detailedValidation.childSKUAvailability.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No child SKUs to validate
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>


    </Box>
  );
};

export default StockValidator;